import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { createPortal } from 'react-dom';
import { Briefcase, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../ThemeProvider';
import { businessTokens } from './businessTheme';
import { BusinessService } from '../../services/BusinessService';
import {
  PITCH_MAX_LENGTH, BUSINESS_NAME_MAX_LENGTH, BUSINESS_FEE_USD,
  CUSTOMER_BASE_MAX_KEYWORDS, describeCooldown, parseCustomerBase,
} from '../../types/business';

/**
 * Register a business — the pitch, submitted before any money changes hands.
 *
 * Two fields only. There is no business name, no portfolio and no store here on
 * purpose: those come after approval AND payment, in the profile builder. A
 * denial should cost the applicant a paragraph, not an afternoon of uploads.
 */
export function BusinessRegistrationModal({
  isOpen,
  onClose,
  ownerId,
  cooldownUntil,
  onSubmitted,
}: {
  isOpen: boolean;
  onClose: () => void;
  ownerId: string;
  /** Set when this owner was recently denied or deleted a profile. */
  cooldownUntil?: any;
  onSubmitted?: (registrationId: string) => void;
}) {
  const { theme } = useTheme();
  const t = businessTokens(theme);

  const [businessName, setBusinessName] = useState('');
  const [pitchText, setPitchText] = useState('');
  const [shopLink, setShopLink] = useState('');
  const [socialLink, setSocialLink] = useState('');
  const [customerBase, setCustomerBase] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const cooling = describeCooldown(cooldownUntil);
  const customerKeywords = parseCustomerBase(customerBase);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    const res = await BusinessService.submitRegistration(ownerId, {
      businessName, pitchText, shopLink, socialLink, customerBase,
    });
    setSubmitting(false);
    if (!res.success) {
      toast.error(res.error || 'Could not submit your application.');
      return;
    }
    toast.success('Application submitted. We will let you know once it has been reviewed.');
    setBusinessName('');
    setPitchText('');
    setShopLink('');
    setSocialLink('');
    setCustomerBase('');
    onSubmitted?.(res.data!.registrationId);
    onClose();
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: t.inset,
    border: `1px solid ${t.border}`,
    borderRadius: 12,
    padding: '12px 14px',
    color: t.text,
    fontSize: 14,
    outline: 'none',
    fontFamily: 'inherit',
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 999997,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 520, maxHeight: '86vh', overflowY: 'auto',
              background: t.surface, border: `1px solid ${t.border}`,
              borderRadius: 20, padding: 20,
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <Briefcase style={{ width: 22, height: 22, color: t.accent }} />
              <h2 style={{ margin: 0, flex: 1, fontSize: 19, fontWeight: 700, color: t.text }}>
                Register your business
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}
              >
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>

            {cooling ? (
              <div
                style={{
                  background: 'rgba(231,76,60,0.12)', border: `1px solid ${t.danger}44`,
                  borderRadius: 12, padding: 14,
                }}
              >
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: t.danger }}>
                  You can apply again in {cooling}.
                </p>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: t.textMuted }}>
                  This applies after an application is declined, or after you delete a
                  business profile you already built.
                </p>
              </div>
            ) : (
              <>
                <p style={{ margin: '0 0 16px', fontSize: 13, lineHeight: 1.5, color: t.textMuted }}>
                  Tell us what your business is and what you would like to do on AfroYap. We
                  read every application. If you are approved, there is a one-time
                  ${BUSINESS_FEE_USD} fee, and then you build your profile: portfolio, posts
                  and a store.
                </p>

                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6 }}>
                  Business name
                </label>
                <input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value.slice(0, BUSINESS_NAME_MAX_LENGTH))}
                  placeholder="What your business is called"
                  style={inputStyle}
                />
                <p style={{ margin: '6px 0 16px', fontSize: 11, color: t.textMuted }}>
                  Your own branding. It does not have to match your AfroYap name.
                </p>

                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6 }}>
                  About your business
                </label>
                <textarea
                  value={pitchText}
                  onChange={(e) => setPitchText(e.target.value.slice(0, PITCH_MAX_LENGTH))}
                  placeholder="What do you sell or make, who is it for, and what would you like to do here?"
                  rows={7}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0 16px' }}>
                  <span style={{ fontSize: 11, color: t.textMuted }}>At least 30 characters.</span>
                  <span style={{ fontSize: 11, color: t.textMuted }}>
                    {pitchText.length}/{PITCH_MAX_LENGTH}
                  </span>
                </div>

                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6 }}>
                  Customer base <span style={{ fontWeight: 400, color: t.textMuted }}>(optional)</span>
                </label>
                <input
                  value={customerBase}
                  onChange={(e) => setCustomerBase(e.target.value)}
                  placeholder="natural hair, students, uk delivery"
                  style={inputStyle}
                />
                {/* The parsed result, not the raw text: this is what actually
                    gets stored, so showing it back is the only way someone can
                    tell their commas landed where they meant them to. */}
                {customerKeywords.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {customerKeywords.map(keyword => (
                      <span
                        key={keyword}
                        style={{
                          background: t.accentSoft, color: t.accent,
                          fontSize: 11, fontWeight: 600,
                          padding: '4px 10px', borderRadius: 999,
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                )}
                <p style={{ margin: '6px 0 16px', fontSize: 11, color: t.textMuted }}>
                  Who you sell to, separated by commas. Up to {CUSTOMER_BASE_MAX_KEYWORDS}.
                </p>

                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6 }}>
                  Your shop or website
                </label>
                <input
                  value={shopLink}
                  onChange={(e) => setShopLink(e.target.value)}
                  placeholder="yourshop.com"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  style={inputStyle}
                />
                <p style={{ margin: '6px 0 16px', fontSize: 11, color: t.textMuted }}>
                  Where customers actually buy. You can confirm or change this when you build
                  your profile.
                </p>

                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: t.text, marginBottom: 6 }}>
                  Social media <span style={{ fontWeight: 400, color: t.textMuted }}>(optional)</span>
                </label>
                <input
                  value={socialLink}
                  onChange={(e) => setSocialLink(e.target.value)}
                  placeholder="instagram.com/yourbusiness"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  style={inputStyle}
                />
                <p style={{ margin: '6px 0 18px', fontSize: 11, color: t.textMuted }}>
                  Helps us see your business is real. It stays with your application and is
                  not shown on your profile.
                </p>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{
                    width: '100%', height: 48, borderRadius: 14, border: 'none',
                    background: t.buttonBg, color: t.buttonText,
                    fontSize: 15, fontWeight: 700,
                    cursor: submitting ? 'default' : 'pointer',
                    opacity: submitting ? 0.7 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                >
                  {submitting && <Loader2 style={{ width: 16, height: 16 }} className="animate-spin" />}
                  {submitting ? 'Submitting' : 'Submit application'}
                </button>

                <p style={{ margin: '10px 0 0', fontSize: 11, color: t.textMuted, textAlign: 'center' }}>
                  Nothing is charged now. You only pay if you are approved.
                </p>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
